import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaEdit, FaTrash } from "react-icons/fa";
import BillToPartyModal from "../components/BillToPartyModal";
import { PartyAvatar } from "../components/BillToPartyTag";
import { useConfirm, useNotify } from "../components/ConfirmModal";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import { deactivateBillToParty, fetchBillToParties, taxIdLines } from "../services/billToService";
import { formatShortAddress } from "../utils/formatters";
import "../assets/css/DigixLeads.css";
import "../assets/css/FinanceAccounts.css";
import "../assets/css/ViewCustomer.css";

const ROWS_PER_PAGE = 10;

function formatDateDisplay(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function phoneOf(row) {
  return row.Phone ? [row.Country_Code, row.Phone].filter(Boolean).join(" ") : "";
}

/** Finance Management > Bill To Parties: every company/individual invoices can be addressed to. */
function BillToPartiesTab() {
  const confirm = useConfirm();
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [kindFilter, setKindFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalParty, setModalParty] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const list = await fetchBillToParties();
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      setListError(err.response?.data?.error || err.message || "Could not load bill to parties.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rows.filter((r) => {
      const status = String(r.Status || "").toLowerCase();
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (kindFilter === "self" && !r.Is_Self) return false;
      if (kindFilter === "external" && r.Is_Self) return false;
      if (kindFilter === "individual" && r.Party_Type !== "Individual") return false;
      if (kindFilter === "organization" && r.Party_Type !== "Organization") return false;
      if (!q) return true;
      const hay = [
        r.Name,
        r.Contact_Person,
        r.Email,
        phoneOf(r),
        r.GSTIN,
        r.PAN,
        r.Tax_Id,
        r.Country_Name,
        r.Address,
        r.Self_Tenant_Name
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [rows, searchText, statusFilter, kindFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter, kindFilter]);

  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  const startRow = totalRows === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1;
  const endRow = Math.min(page * ROWS_PER_PAGE, totalRows);

  const openCreate = () => {
    setModalParty(null);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setModalParty(row);
    setShowModal(true);
  };

  const handleSaved = async (saved) => {
    setShowModal(false);
    await load();
    notify?.(saved?.Bill_To_Id && modalParty ? "Bill To party updated." : "Bill To party created.");
  };

  const handleDeactivate = async (row) => {
    if (!row?.Bill_To_Id || row.Is_Self) return;
    const ok = await confirm({
      title: "Deactivate Bill To",
      message: `Deactivate "${row.Name}"? Existing invoices keep their details; clients defaulting to it fall back to their own company.`
    });
    if (!ok) return;
    setBusyId(row.Bill_To_Id);
    try {
      const res = await deactivateBillToParty(row.Bill_To_Id);
      await load();
      notify?.(res?.message || "Bill To party deactivated.");
    } catch (err) {
      console.error(err);
      notify?.(err.response?.data?.error || "Failed to deactivate bill to party.");
    } finally {
      setBusyId(null);
    }
  };

  const kindTags = (row) =>
    row.Is_Self ? (
      <span className="bill-to-name-cell__tags">
        <span className="bill-to-tag bill-to-tag--self" title={row.Self_Tenant_Name || ""}>
          Own company
        </span>
      </span>
    ) : null;

  return (
    <div className="finance-accounts bill-to-parties">
      <div className="search-actions-row finance-accounts-search-actions-row">
        <div className="search-filters-row">
          <input
            className="search-filter-input"
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search name, email, GSTIN, EIN/VAT, country..."
          />
          <select
            className="search-filter-input search-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            className="search-filter-input search-filter-select"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
          >
            <option value="all">Kind</option>
            <option value="self">Clients' own companies</option>
            <option value="external">Other parties</option>
            <option value="organization">Companies</option>
            <option value="individual">Individuals</option>
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              setSearchText("");
              setStatusFilter("active");
              setKindFilter("all");
            }}
          >
            Clear
          </button>
        </div>
        <div className="finance-accounts-actions header-actions">
          <button type="button" className="btn-primary btn-add-lead btn-new-company-account" onClick={openCreate}>
            New Bill To
          </button>
        </div>
      </div>

      {listError && <div className="finance-accounts-alert finance-accounts-alert--error">{listError}</div>}

      {loading ? (
        <div className="finance-accounts-loading">Loading bill to parties...</div>
      ) : (
        <>
          <div className="finance-accounts-table-wrap co-desktop-table">
            <table className="finance-accounts-table">
              <thead>
                <tr>
                  <th className="col-sticky col-0" style={{ minWidth: "200px" }}>Name</th>
                  <th style={{ minWidth: "140px" }}>Contact</th>
                  <th style={{ minWidth: "180px" }}>Email</th>
                  <th style={{ minWidth: "130px" }}>Phone</th>
                  <th style={{ minWidth: "190px" }}>Tax IDs</th>
                  <th style={{ minWidth: "120px" }}>Country</th>
                  <th style={{ minWidth: "220px" }}>Address</th>
                  <th style={{ minWidth: "100px" }}>Status</th>
                  <th style={{ minWidth: "110px" }}>Updated</th>
                  <th className="col-sticky col-action" style={{ minWidth: "110px" }}>
                    <div style={{ justifyContent: "center" }}>Action</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="finance-accounts-empty">
                      No bill to parties match your filters.
                    </td>
                  </tr>
                ) : (
                  paginated.map((row) => {
                    const status = row.Status || "—";
                    return (
                      <tr key={row.Bill_To_Id}>
                        <td className="col-sticky col-0">
                          <div className="bill-to-name-cell">
                            <PartyAvatar party={row} />
                            <div className="bill-to-name-cell__body">
                              <span
                                className="table-name-link"
                                role="button"
                                tabIndex={0}
                                onClick={() => openEdit(row)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    openEdit(row);
                                  }
                                }}
                              >
                                {row.Name || "—"}
                              </span>
                              {kindTags(row)}
                            </div>
                          </div>
                        </td>
                        <td>{row.Contact_Person || "—"}</td>
                        <td>{row.Email || "—"}</td>
                        <td className="finance-accounts-mono">{phoneOf(row) || "—"}</td>
                        <td className="finance-accounts-mono">
                          {taxIdLines(row).length
                            ? taxIdLines(row).map((l) => <div key={l}>{l}</div>)
                            : "—"}
                        </td>
                        <td>{row.Country_Name || "—"}</td>
                        <td title={row.Address || ""}>{formatShortAddress(row) || row.Address || "—"}</td>
                        <td>
                          <span
                            className={`finance-accounts-status finance-accounts-status--${String(status)
                              .toLowerCase()
                              .replace(/\s/g, "-")}`}
                          >
                            {status}
                          </span>
                        </td>
                        <td>{formatDateDisplay(row.Updated_On || row.Created_On)}</td>
                        <td className="col-sticky col-action" style={{ textAlign: "center" }}>
                          <div className="finance-account-actions-cell">
                            <button
                              type="button"
                              className="finance-account-action-btn finance-account-action-btn-edit"
                              title="Edit"
                              onClick={() => openEdit(row)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="finance-account-action-btn finance-account-action-btn-delete"
                              title={row.Is_Self ? "A client's own company cannot be deactivated" : "Deactivate"}
                              onClick={() => handleDeactivate(row)}
                              disabled={row.Is_Self || busyId === row.Bill_To_Id || status === "Inactive"}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <ResultsMobileList emptyLabel="No bill to parties match your filters." className="finance-accounts-mobile">
            {paginated.map((row) => {
              const status = row.Status || "—";
              const statusTone = String(status).toLowerCase() === "active" ? "success" : "neutral";
              return (
                <ResultsMobileCard
                  key={row.Bill_To_Id}
                  title={
                    <span className="bill-to-mcard-title">
                      <PartyAvatar party={row} size="sm" />
                      <span>{row.Name || "—"}</span>
                    </span>
                  }
                  subtitle={kindTags(row)}
                  meta={[{ label: status, tone: statusTone }]}
                  fields={[
                    { label: "Contact", value: row.Contact_Person },
                    { label: "Email", value: row.Email },
                    { label: "Phone", value: phoneOf(row) },
                    { label: "Tax IDs", value: taxIdLines(row).join(" · ") },
                    { label: "Country", value: row.Country_Name },
                    { label: "Address", value: formatShortAddress(row) || row.Address }
                  ]}
                  onOpen={() => openEdit(row)}
                  actions={
                    <div className="co-mcard__action-grid">
                      <button type="button" className="co-maction" onClick={() => openEdit(row)}>
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction co-maction--danger"
                        onClick={() => handleDeactivate(row)}
                        disabled={row.Is_Self || busyId === row.Bill_To_Id || status === "Inactive"}
                      >
                        <FaTrash size={14} />
                        <span>Deactivate</span>
                      </button>
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
        </>
      )}

      {!loading && totalPages > 1 && (
        <div className="pagination">
          <button
            type="button"
            className="pagination-btn pagination-arrow"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            aria-label="Previous page"
          >
            <FaChevronLeft />
          </button>
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <span
                key={p}
                role="button"
                tabIndex={0}
                className={`page-number ${page === p ? "active" : ""}`}
                onClick={() => setCurrentPage(p)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setCurrentPage(p);
                  }
                }}
              >
                {p}
              </span>
            ))}
          </div>
          <button
            type="button"
            className="pagination-btn pagination-arrow"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            aria-label="Next page"
          >
            <FaChevronRight />
          </button>
          <div className="pagination-info">
            {startRow}-{endRow} of {totalRows} items
          </div>
        </div>
      )}

      {showModal ? (
        <BillToPartyModal party={modalParty} onSaved={handleSaved} onClose={() => setShowModal(false)} />
      ) : null}
    </div>
  );
}

export default BillToPartiesTab;
